import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const GetRequest = () => {
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const adminToken =
    useSelector((state) => state.auth.adminToken) ||
    localStorage.getItem("adminToken");

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true); // Start loading
        if (!adminToken) {
          console.warn("Admin token is missing");
          setError("Authentication token is missing. Please log in.");
          setLoading(false);
          return;
        }

        const myHeaders = new Headers();
        myHeaders.append("Authorization", `Bearer ${adminToken}`);

        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow",
        };

        console.log("Fetching requests with admin token:", adminToken);

        const response = await fetch(
          "https://backend.pmc-group.uk/api/admin/request",
          requestOptions
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Fetched raw data:", data);

        if (Array.isArray(data.data)) {
          console.log("Fetched requests:", data.data);
          setRequests(data.data);
        } else {
          console.warn("Fetched data is not an array:", data.data);
          setRequests([]);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        setError("Failed to fetch requests");
      } finally {
        setLoading(false); // End loading
      }
    };

    fetchRequests();
  }, [adminToken]);

  return (
    <div className="p-0 m-0 box-border">
      <div className="w-[90%] mx-auto pt-1 lg:pt-4 pb-12">
        <div className="w-full mx-auto py-8 shadow-xl bg-white rounded-lg">
          <div className="flex justify-between items-start mx-6 my-6">
            <h2 className="text-xl text-navBg font-semibold px-6 py-4">
              Service Requests
            </h2>
            <div className="w-[200px] border-2 border-navBg p-2 rounded-md">
              <input
                type="text"
                placeholder="Search by email"
                className="w-full outline-none"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-gray-50 min-h-auto">
            {error ? (
              <div className="text-red-500">{error}</div>
            ) : loading ? (
              // Skeleton loader
              <div className="p-4">
                <Skeleton count={5} height={40} />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center text-gray-500 p-4">
                No requests found.
              </div>
            ) : (
              <div className="p-4 overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gradient-to-tr from-navBg to-purple-600 text-white">
                      <th className="py-2 px-4 text-left">Id</th>
                      <th className="py-2 px-4 text-left">Date</th>
                      <th className="py-2 px-4 text-left">Name</th>
                      <th className="py-2 px-4 text-left">Contact Date</th>
                      <th className="py-2 px-4 text-left">Contact Time</th>
                      <th className="py-2 px-4 text-left">Service Type</th>
                      <th className="py-2 px-4 text-left">Email</th>
                      <th className="py-2 px-4 text-left">Description</th>
                      <th className="py-2 px-4 text-left">Image</th>
                      <th className="py-2 px-4 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests
                      .filter(
                        (req) =>
                          req.user?.email &&
                          req.user.email
                            .toLowerCase()
                            .includes(search.toLowerCase())
                      )
                      .map((request) => (
                        <tr
                          key={request.id}
                          className="border-t text-sm font-normal hover:bg-gray-100"
                        >
                          <td className="py-2 px-4">{request.id}</td>
                          <td className="py-2 px-4">
                            {new Date(request.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-2 px-4">{request.full_name}</td>
                          <td className="py-2 px-4">
                            {new Date(
                              request.contact_date
                            ).toLocaleDateString()}
                          </td>
                          <td className="py-2 px-4">{request.contact_time}</td>
                          <td className="py-2 px-4">
                            {request.service_type?.name}
                          </td>
                          <td className="py-2 px-4">{request.user?.email}</td>
                          <td className="py-2 px-4">{request.description}</td>
                          <td className="py-2 px-4">
                            {request.image ? (
                              <img
                                src={request.image}
                                alt="Request"
                                className="w-12 h-12 object-cover rounded-md"
                              />
                            ) : (
                              <span>No Image</span>
                            )}
                          </td>
                          <td className="py-2 px-4">
                            <select
                              className="outline-none"
                              defaultValue={request.status}
                            >
                              <option value="pending">Pending</option>
                              <option value="call-placed">Call Placed</option>
                              <option value="quotation-review">
                                Quotation Review
                              </option>
                              <option value="job-in-progress">
                                Job In Progress
                              </option>
                              <option value="completed">Completed</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end items-end gap-2 mx-4 py-6">
            <div className="flex justify-center items-center w-[40px] h-[40px] cursor-pointer rounded-full bg-gray-200 hover:bg-gray-500 hover:text-white">
              <FaArrowLeft />
            </div>
            <div className="flex justify-center items-center w-[40px] h-[40px] cursor-pointer rounded-full bg-gray-200 hover:bg-gray-500 hover:text-white">
              <FaArrowRight />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetRequest;
